<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import { formatAmount, toIsoDate } from '../utils/format'
import type { FinanceReconciliationRow, FinanceReconciliationTotals } from '../types/finance'

const { t } = useI18n()
const { reconciliation } = useFinanceApi()

const rows = ref<FinanceReconciliationRow[]>([])
const totals = ref<FinanceReconciliationTotals | null>(null)
const loading = ref(true)
const error = ref('')
const asOfDate = ref(toIsoDate())

const isReconciled = computed(() => totals.value?.is_reconciled ?? rows.value.every((row) => row.is_reconciled))
const differenceCount = computed(() => rows.value.filter((row) => !row.is_reconciled).length)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await reconciliation(asOfDate.value)
    rows.value = result.rows
    totals.value = result.summary
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <h2>{{ t('finance.nav.reconciliation') }}</h2>
  </div>

  <div class="toolbar">
    <input v-model="asOfDate" class="date-input" type="date">
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('finance.common.apply') }}
    </button>
    <span class="spacer" />
    <div class="summary">
      <span
        class="status-badge"
        :class="isReconciled ? 'status-ok' : 'status-critical'"
      >
        {{ isReconciled ? t('finance.reports.reconciled') : t('finance.reports.notReconciled') }}
      </span>
      <span v-if="differenceCount">
        <strong>{{ differenceCount }}</strong> {{ t('finance.common.currency') }}
      </span>
    </div>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('finance.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('finance.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card accent">
        <span class="summary-label">{{ t('finance.reports.reconciled') }}</span>
        <span class="summary-value">
          {{ isReconciled ? t('finance.common.yes') : t('finance.common.no') }}
        </span>
        <span class="summary-label">{{ t('finance.overview.asOf', { date: asOfDate }) }}</span>
      </div>
      <div class="summary-card" :class="{ attention: !isReconciled }">
        <span class="summary-label">{{ t('finance.reports.paymentPostingDelta') }}</span>
        <span class="summary-value">
          {{ formatAmount(totals?.payment_posting_absolute_delta ?? 0) }}
        </span>
      </div>
    </div>

    <div v-if="rows.length === 0" class="state-message"><p>{{ t('finance.reports.empty') }}</p></div>

    <div v-else class="table-scroll">
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('finance.common.currency') }}</th>
            <th class="cell-num">{{ t('finance.reports.receivablesBalance') }}</th>
            <th class="cell-num">{{ t('finance.reports.payablesBalance') }}</th>
            <th class="cell-num">{{ t('finance.reports.treasuryBalance') }}</th>
            <th class="cell-num">{{ t('finance.reports.activeAccountedPayments') }}</th>
            <th class="cell-num">{{ t('finance.reports.activePaymentPostings') }}</th>
            <th class="cell-num">{{ t('finance.reports.paymentPostingDelta') }}</th>
            <th>{{ t('finance.common.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.currency_code">
            <td class="cell-name cell-code">{{ row.currency_code }}</td>
            <td class="cell-num">{{ formatAmount(row.receivables_balance) }}</td>
            <td class="cell-num">{{ formatAmount(row.payables_balance) }}</td>
            <td class="cell-num">{{ formatAmount(row.treasury_balance) }}</td>
            <td class="cell-num">{{ formatAmount(row.active_accounted_payments) }}</td>
            <td class="cell-num">{{ formatAmount(row.active_payment_postings) }}</td>
            <td class="cell-num" :class="Number(row.payment_posting_delta) === 0 ? '' : 'is-negative'">
              {{ formatAmount(row.payment_posting_delta) }}
            </td>
            <td>
              <span
                class="status-badge"
                :class="row.is_reconciled ? 'status-ok' : 'status-critical'"
              >
                {{ row.is_reconciled ? t('finance.reports.reconciled') : t('finance.reports.notReconciled') }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </template>
</template>
